import { say } from "../../words.ts";

/**
 * The line under the minimap's field that says where the player is (P1.9-T197, ADR-061's note of 2026-10-15): the name
 * of the place and the tile the player stands in, "Willowmere: 120, 88". The tile is the editor's cell number, so a
 * builder and a player name the same spot; the name is what the page already holds (the map's region, else the map's
 * own name) and nothing is sent for it.
 */

/**
 * The tile a position in tiles falls in: its floor, as the editor numbers a cell. A position that is not a finite number
 * reads as 0.
 *
 * @param position Tiles across or down, as the minimap holds the player's position.
 * @returns The whole tile.
 */
export function tileOf(position: number): number {
  return Number.isFinite(position) ? Math.floor(position) : 0;
}

/**
 * The name the line carries: the region's name where the map has one, else the map's own name. This is the one place a
 * later source of a zone's name would be read (ADR-061's note of 2026-10-15).
 *
 * @param region The map's region from the manifest (`regionOf`), or undefined for a map with none.
 * @param title The map's own name (`mapTitle`).
 * @returns The name; the title when the region has none.
 */
export function placeName(region: { readonly name: string } | undefined, title: string): string {
  return region !== undefined && region.name !== "" ? region.name : title;
}

/**
 * The line's two parts for a place and a position: the name before its colon (empty for no name) and the tile's two
 * numbers, and the whole text they make side by side. The parts are set in two elements so a long name is cut short
 * and the numbers never are.
 */
export interface PlaceParts {
  readonly name: string;
  readonly tile: string;
  readonly text: string;
}

/**
 * The line for a place and a position.
 *
 * @param name The place's name, empty for none.
 * @param x The player's position across, in tiles.
 * @param y The player's position down, in tiles.
 * @returns The parts: "Willowmere:" and "120, 88" with x and y whole, or the tile alone for no name.
 */
export function placeParts(name: string, x: number, y: number): PlaceParts {
  const label = name === "" ? "" : say("minimap.placeName", { name });
  const tile = say("minimap.placeTile", { x: String(tileOf(x)), y: String(tileOf(y)) });
  return { name: label, tile, text: label === "" ? tile : `${label} ${tile}` };
}

/**
 * The line's text for a place and a position.
 *
 * @param name The place's name, empty for none.
 * @param x The player's position across, in tiles.
 * @param y The player's position down, in tiles.
 * @returns "name: x, y" with x and y whole, or "x, y" for no name.
 */
export function placeLine(name: string, x: number, y: number): string {
  return placeParts(name, x, y).text;
}

/**
 * Decides when the line is written again: only when the tile or the name differs from what was last given, so a frame
 * in which neither changed allocates nothing.
 *
 * @example
 * const line = new PlaceLine();
 * const text = line.next("Willowmere", 120.5, 88.5);
 */
export class PlaceLine {
  private name: string | null = null;
  private tileX = Number.NaN;
  private tileY = Number.NaN;

  /**
   * Reads a position and name.
   *
   * @param name The place's name.
   * @param x The player's position across, in tiles.
   * @param y The player's position down, in tiles.
   * @returns The new parts when the tile or the name changed since the last parts given, else null.
   */
  public next(name: string, x: number, y: number): PlaceParts | null {
    const tx = tileOf(x);
    const ty = tileOf(y);
    if (name === this.name && tx === this.tileX && ty === this.tileY) {
      return null;
    }
    this.name = name;
    this.tileX = tx;
    this.tileY = ty;
    return placeParts(name, tx, ty);
  }

  /**
   * Forgets what was last given, so the next reading gives its parts.
   */
  public reset(): void {
    this.name = null;
    this.tileX = Number.NaN;
    this.tileY = Number.NaN;
  }
}

/**
 * Keeps two elements' text equal to the line for a place and the player's position: writes it now and again at each
 * move, only when the tile or the name changed.
 *
 * @param place The place's name, empty for none.
 * @param position The player's position, read at each move and never copied.
 * @param onMove Subscribes a listener to the player's moves and returns how to stop.
 * @param name The element that holds the name.
 * @param tile The element that holds the tile.
 * @returns Stops listening.
 */
export function bindPlaceLine(place: string, position: { readonly x: number; readonly y: number }, onMove: (listener: () => void) => () => void, name: { textContent: string | null } | null, tile: { textContent: string | null } | null): () => void {
  const line = new PlaceLine();
  /**
   * Writes the line when the tile or the name differs from what it shows.
   */
  const write = (): void => {
    const parts = line.next(place, position.x, position.y);
    if (parts === null || name === null || tile === null) {
      return;
    }
    name.textContent = parts.name;
    tile.textContent = parts.tile;
  };
  write();
  return onMove(write);
}
